import type { Locale } from "@borealis/layout-express";
import { Trans, useLingui } from "@lingui/react";
import type { CarAd } from "@mobility-item-page/types";
import { formatNumber } from "@mobility-item-page/utilities";
import type { ReactNode } from "react";
import { usesScandinavianMiles } from "src/utils/brand.ts";
import { useAppConfig } from "../../../context/AppConfigProviderContext.tsx";
import { Icon } from "../Icon.tsx";
import { ScreenReaderSeparator } from "../ScreenReaderSeparator.tsx";
import { ChargingPlugs } from "./ChargingPlugs.tsx";
import { EvExpandableRow } from "./EvExpandableRow.tsx";
import { EvFaqLinks } from "./EvFaqLinks.tsx";
import { EvInfoCard } from "./EvInfoCard.tsx";

const GREEN_TECHNOLOGY_DEDUCTION_URL =
  "https://www.skatteverket.se/privat/fastigheterochbostad/gronteknik.4.676f4884175c97df4192860.html";

type Props = {
  ad: CarAd;
};

/**
 * Branch key for the ICU `select` in the messages below. Must be a string: `<Trans>` turns any
 * non-string, non-number value into a component placeholder, so a boolean would always fall
 * through to `other`.
 */
type UsesMiles = "true" | "false";

/** Running costs always get two decimals, so a rate does not read as "4 kr/mil" next to "3,75 kr/mil". */
const formatRate = (amount: number | undefined, locale: Locale) => {
  const minimumFractionDigits = Number.isInteger(amount) ? 0 : 2;
  return formatNumber(amount, locale, { minimumFractionDigits });
};

export const ChargingCostCard = ({ ad }: Props) => {
  const { i18n } = useLingui();
  const { locale, brand } = useAppConfig();

  const chargingCosts = ad.charging_costs;

  if (!chargingCosts) {
    return null;
  }

  const usesMiles: UsesMiles = usesScandinavianMiles(brand) ? "true" : "false";

  const {
    ac_charge_power_kw: acPower,
    ac_charge_time_min: acTime,
    dc_charge_power_kw: dcPower,
    dc_charge_time_min: dcTime,
  } = ad.vehicle_charging_properties ?? {};
  // A ballpark, so "about 7.3 hours" would read as falsely exact
  const acChargeHours = acTime != null ? Math.round(acTime / 30) / 2 : undefined;

  const formatChargeTime = (minutes: number) =>
    i18n.t({
      id: "itemPageLayout.ev.chargingCost.chargeTime.value",
      message: "{minutes} mins",
      comment:
        "Charging time value on electric car ads, always in minutes. 'mins' is short for minutes — a rapid charge is never quick enough for the singular to come up.",
      values: { minutes: formatNumber(minutes, locale) },
    });

  const formatPower = (power: number) =>
    i18n.t({
      id: "itemPageLayout.ev.chargingCost.power.value",
      message: "{power} kW",
      comment: "Charging power value with its unit, on the charging card on electric car ads",
      values: { power: formatNumber(power, locale) },
    });

  return (
    <EvInfoCard
      testId="ev-charging-cost"
      title={i18n.t({
        id: "itemPageLayout.ev.chargingCost.title",
        message: "Charging",
        comment: "Heading of the charging cost card on electric car ads",
      })}
    >
      <ChargingPlugs plugTypes={ad.vehicle_charging_properties?.plug_types} />

      <div className="mb-24 border-t [&>div]:border-b">
        {/* Only where the figures are a worked example. A car with its own has the savings
         * calculator card above instead, which covers the same ground and lets the reader put
         * their own prices to it.
         */}
        {chargingCosts.source === "EXAMPLE" && (
          <EvExpandableRow
            title={i18n.t({
              id: "itemPageLayout.ev.chargingCost.chargingCost.title",
              message: "Charging cost",
              comment: "Heading of the charging cost row on electric car ads",
            })}
            value={i18n.t({
              id: "itemPageLayout.ev.chargingCost.chargingCost.value",
              message: "{usesMiles, select, true {from {cost_pr_mile} kr/mil} other {from {cost_pr_km} kr/km}}",
              comment:
                "Charging cost value on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA).",
              values: {
                usesMiles,
                cost_pr_mile: formatRate(chargingCosts.home_charging.cost_pr_scandinavian_mile, locale),
                cost_pr_km: formatRate(chargingCosts.home_charging.cost_pr_km, locale),
              },
            })}
            body={<ChargingCostRowBody chargingCosts={chargingCosts} usesMiles={usesMiles} />}
            trackingName="chargingCost"
          />
        )}
        {acPower != null && (
          <EvExpandableRow
            title={i18n.t({
              id: "itemPageLayout.ev.chargingCost.acChargePower.title",
              message: "Home charging (AC)",
              comment: "Heading of the AC charging power row on electric car ads",
            })}
            value={formatPower(acPower)}
            body={
              acChargeHours
                ? i18n.t({
                    id: "itemPageLayout.ev.chargingCost.acChargePower.body.withChargeTime",
                    message:
                      "The fastest the car can charge from an AC charger, which is what a home charging box and most parking chargers provide. At {power} kW a full charge from empty takes about {hours, plural, one {{formattedHours} hour} other {{formattedHours} hours}}, so overnight at home is comfortable. The limit sits in the car's own charger, not the box, so a more powerful box will not charge it faster.",
                    comment:
                      "Body text of the AC charging power row on electric car ads, when the time for a full charge is known. 'hours' is that time, rounded to the half hour.",
                    values: {
                      power: formatNumber(acPower, locale),
                      hours: acChargeHours,
                      formattedHours: formatNumber(acChargeHours, locale),
                    },
                  })
                : i18n.t({
                    id: "itemPageLayout.ev.chargingCost.acChargePower.body",
                    message:
                      "The fastest the car can charge from an AC charger, which is what a home charging box and most parking chargers provide. AC charging is slow and steady, meant for hours parked at home or at work rather than a quick top-up. Usually done overnight. The limit sits in the car's own charger, not the box, so a more powerful box will not charge it faster.",
                    comment:
                      "Body text of the AC charging power row on electric car ads, when the time for a full charge is not known",
                  })
            }
            trackingName="acChargePower"
          />
        )}

        {dcPower != null && (
          <EvExpandableRow
            title={i18n.t({
              id: "itemPageLayout.ev.chargingCost.dcChargePower.title",
              message: "Public charging (DC)",
              comment: "Heading of the DC charging power row on electric car ads",
            })}
            value={formatPower(dcPower)}
            body={i18n.t({
              id: "itemPageLayout.ev.chargingCost.dcChargePower.body",
              message:
                "The fastest the car can charge from a public fast charger. It only holds that peak for part of the charge and slows as the battery fills, so the 10-80% time is the more useful number to compare. A more powerful charger will not push it higher.",
              comment: "Body text of the DC charging power row on electric car ads",
            })}
            trackingName="dcChargePower"
          />
        )}

        {dcTime != null && (
          <EvExpandableRow
            title={i18n.t({
              id: "itemPageLayout.ev.chargingCost.chargingSpeed.title",
              message: "Charging time 10-80% (DC)",
              comment: "Heading of the DC charging time row on electric car ads",
            })}
            value={formatChargeTime(dcTime)}
            body={i18n.t({
              id: "itemPageLayout.ev.chargingCost.chargingSpeed.body",
              message:
                "A fast charge from 10% to 80% with a warm battery and a charger running at full power. Charging slows sharply above 80%, which is why the number stops there, and the last 20% can take as long as the first 70%. A cold battery or a busy charging station will add time.",
              comment:
                "Body text of the DC charging speed row on electric car ads. 10-80% is the industry-standard window for quoting rapid charging.",
            })}
            trackingName="chargingSpeed"
          />
        )}

        <EvExpandableRow
          title={i18n.t({
            id: "itemPageLayout.ev.chargingCost.homeChargingSetup.title",
            message: "Home charging setup",
            comment: "Heading of the home charging setup row on electric car ads",
          })}
          body={<HomeChargingSetupRowBody usesMiles={usesMiles} />}
          trackingName="homeCharging"
        />
      </div>
      <EvFaqLinks links={ad.ev_info_links?.charging} />
    </EvInfoCard>
  );
};

const ChargingCostRowBody = ({
  chargingCosts,
  usesMiles,
}: {
  chargingCosts: CarAd["charging_costs"];
  usesMiles: UsesMiles;
}) => {
  const { locale } = useAppConfig();

  if (!chargingCosts) {
    return null;
  }

  const carKmPrKWh = formatNumber(chargingCosts.range_pr_kwh, locale);

  const homePricePrKWh = formatRate(chargingCosts.home_charging.price_pr_kwh, locale);
  const homeCostPrKm = formatRate(chargingCosts.home_charging.cost_pr_km, locale);
  const homeCostPrMile = formatRate(chargingCosts.home_charging.cost_pr_scandinavian_mile, locale);

  const kwhPrMile = formatNumber(chargingCosts.kwh_pr_scandinavian_mile, locale);

  return (
    <>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body"
          message="Driving an electric car is generally cheaper than driving a gas-powered car — especially if you're able to charge at home."
          comment="Body of the charging cost row on electric car ads"
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.homeCharging.title"
          message="Home charging"
          comment="Sub heading in the charging cost row on electric car ads"
        />
      </p>
      <p className="whitespace-pre-line">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.homeCharging.example"
          message={
            "At home, you pay your normal electricity price per kWh.\n {usesMiles, select, true {A typical electric car uses about {kwh_pr_mile} kWh per mil, so at an example price of {example_price} kr per kWh, you would pay <0>{cost_pr_mile} kr/mil</0>.} other {A typical electric car drives about {car_km_pr_kWh} km per kWh, so at an example price of {example_price} kr per kWh, you would pay <0>{cost_pr_km} kr/km</0>.}}"
          }
          comment="Body of the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA). <0> makes the cost bold."
          values={{
            usesMiles,
            car_km_pr_kWh: carKmPrKWh,
            kwh_pr_mile: kwhPrMile,
            example_price: homePricePrKWh,
            cost_pr_km: homeCostPrKm,
            cost_pr_mile: homeCostPrMile,
          }}
          components={{ 0: <strong /> }}
        />
      </p>
      <br />
      <p className="text-xs">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.homeCharging.note"
          message="{usesMiles, select, true {Price per mil = kWh/mil × Price/kWh} other {Price per km = (1 ÷ km/kWh) × Price/kWh}}"
          comment="Formula note in the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA)."
          values={{ usesMiles }}
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.publicCharging.title"
          message="Public charging"
          comment="Sub heading in the charging cost row on electric car ads"
        />
      </p>
      <p className="whitespace-pre-line">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.publicCharging.body"
          message="Public rapid chargers add a markup, so they cost more — often around {example_price} kr per kWh. {usesMiles, select, true {At {kwh_pr_mile} kWh per mil, you would pay <0>{cost_pr_mile} kr/mil</0>.} other {At {car_km_pr_kWh} km per kWh, you would pay <0>{cost_pr_km} kr/km</0>.}} Some networks offer lower rates with a membership."
          comment="Body of the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA). <0> makes the cost bold."
          values={{
            usesMiles,
            example_price: formatRate(chargingCosts.public_charging.price_pr_kwh, locale),
            car_km_pr_kWh: carKmPrKWh,
            kwh_pr_mile: kwhPrMile,
            cost_pr_km: formatRate(chargingCosts.public_charging.cost_pr_km, locale),
            cost_pr_mile: formatRate(chargingCosts.public_charging.cost_pr_scandinavian_mile, locale),
          }}
          components={{ 0: <strong /> }}
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.gasPoweredCar.title"
          message="Gas-powered car"
          comment="Sub heading in the charging cost row on electric car ads"
        />
      </p>
      <p className="whitespace-pre-line">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.gasPoweredCar.body"
          message="{usesMiles, select, true {A gas-powered car using {l_pr_mile} L/mil, with an example price of {example_price} kr/L, costs <0>{cost_pr_mile} kr/mil</0>.} other {A gas-powered car doing {km_pr_l} km/L, with an example price of {example_price} kr/L, costs <0>{cost_pr_km} kr/km</0>.}}"
          comment="Body of the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA). <0> makes the cost bold."
          values={{
            usesMiles,
            km_pr_l: formatNumber(chargingCosts.petrol_comparison.km_pr_liter, locale),
            l_pr_mile: formatNumber(chargingCosts.petrol_comparison.liters_pr_scandinavian_mile, locale),
            example_price: formatRate(chargingCosts.petrol_comparison.price_pr_liter, locale),
            cost_pr_km: formatRate(chargingCosts.petrol_comparison.cost_pr_km, locale),
            cost_pr_mile: formatRate(chargingCosts.petrol_comparison.cost_pr_scandinavian_mile, locale),
          }}
          components={{ 0: <strong /> }}
        />
      </p>
      <br />
      <p className="text-xs">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.gasPoweredCar.note"
          message="{usesMiles, select, true {Price per mil = L/mil × Gas price/L} other {Price per km = (1 ÷ km/L) × Gas price/L}}"
          comment="Formula note in the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA)."
          values={{ usesMiles }}
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans id="itemPageLayout.ev.chargingCost.chargingCost.body.savings.title" message="Savings per month" />
      </p>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.savings.body"
          message="{usesMiles, select, true {Home charging costs about {saving_pr_mile} kr per mil less than gas. For someone driving {annual_mile} mil a year, home charging saves you roughly <0>{monthly_amount} kr a month</0> versus gas.} other {Home charging costs about {saving_pr_km} kr per km less than gas. For someone driving {annual_km} km a year, home charging saves you roughly <0>{monthly_amount} kr a month</0> versus gas.}} Charging on public networks will reduce savings."
          comment="Body of the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA). <0> makes the cost bold."
          values={{
            usesMiles,
            saving_pr_km: formatRate(chargingCosts.monthly_savings.saving_pr_km, locale),
            saving_pr_mile: formatRate(chargingCosts.monthly_savings.saving_pr_scandinavian_mile, locale),
            annual_km: formatNumber(chargingCosts.monthly_savings.annual_km, locale),
            annual_mile: formatNumber(chargingCosts.monthly_savings.annual_scandinavian_mile, locale),
            monthly_amount: formatNumber(chargingCosts.monthly_savings.monthly_amount, locale, {
              maximumFractionDigits: 0,
            }),
          }}
          components={{ 0: <strong /> }}
        />
      </p>
    </>
  );
};

const HomeChargingSetupRowBody = ({ usesMiles }: { usesMiles: UsesMiles }) => {
  const { brand } = useAppConfig();

  return (
    <>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body"
          message="Most electric car owners charge at home overnight. Here's what that takes, and what it costs."
          comment="Body of the home charging setup row on electric car ads"
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.whatYouNeed.title"
          message="What you need"
          comment="Sub heading in the home charging setup row on electric car ads"
        />
      </p>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.whatYouNeed.body"
          message="A wallbox (a dedicated home charger), installed by a certified electrician. An ordinary socket is very slow (a full charge can take over a day) and is not recommended for regular use due to safety risks."
          comment="Body of the home charging setup row on electric car ads"
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.sharedParking.title"
          message="Apartment or shared parking"
          comment="Sub heading in the home charging setup row on electric car ads"
        />
      </p>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.sharedParking.body"
          message="In an apartment or shared parking, you'll need approval from your housing association or landlord first. Many are adding charging, so it's worth asking."
          comment="Body of the home charging setup row on electric car ads"
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.cost.title"
          message="What it costs"
          comment="Sub heading in the home charging setup row on electric car ads"
        />
      </p>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.cost.body"
          message="A wallbox plus standard installation (usually up to 10-15 meters of cabling) costs around 10,000–20,000 kr. Older wiring or a fuse-box upgrade can add cost."
          comment="Body of the home charging setup row on electric car ads. Note that the 50% tax deduction is only available in Sweden, and should only be in the swedish translation."
        />
      </p>
      <br />
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.cost.payback"
          message="{usesMiles, select, true {If you drive 1,500 mil a year and charge at home instead of filling up with gas, a wallbox typically pays for itself inside a year.} other {If you drive 15,000 km a year and charge at home instead of filling up with gas, a wallbox typically pays for itself inside a year.}}"
          comment="Body of the home charging setup row on electric car ads. 'usesMiles' is true on the brands that quote distance in Scandinavian mil (FINN, Blocket) and false where it is in km (DBA)."
          values={{ usesMiles }}
        />
      </p>
      {brand === "blocket" && (
        <>
          <br />
          <p>
            <Trans
              id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.greenTechnologyDeduction"
              message="Read more about the current terms and rates for the green technology deduction (grönt avdrag) at <0>skatteverket.se</0>"
              comment="Body of the home charging setup row on electric car ads, only shown on Blocket. <0> is a link to skatteverket.se. Do not translate the name of the deduction in parenthesis, the localised name in the running text is enough."
              components={{ 0: <GreenTechnologyDeductionLink /> }}
            />
          </p>
        </>
      )}
    </>
  );
};

const GreenTechnologyDeductionLink = ({ children }: { children?: ReactNode }) => (
  <a
    href={GREEN_TECHNOLOGY_DEDUCTION_URL}
    target="_blank"
    rel="noopener nofollow"
    className="inline-flex items-center gap-4"
  >
    {children}
    <ScreenReaderSeparator />
    <Icon name="LinkExternal" className="inline-block align-baseline" />
  </a>
);
